import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useWebSocket } from '../context/WebSocketContext';
import { ChainSelector } from './ChainSelector';
import { getIndexerStatus } from '../lib/api';
import {
  Sun, Moon, Search, Activity, Hexagon, ChevronDown, RefreshCw, Wifi, WifiOff,
  Radio, BarChart3, Tag, ShieldCheck, FileText, Menu, X, Calculator, Globe,
  ArrowLeftRight, Wrench,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from './ui/dropdown-menu';

// Primary, always-visible destinations.
const PRIMARY_NAV = [
  { to: '/', label: 'Explorer', icon: Globe, testid: 'nav-explorer-btn' },
  { to: '/swap-simulator', label: 'Swap Simulator', icon: ArrowLeftRight, testid: 'nav-swap-simulator-btn' },
  { to: '/bundle', label: 'Bundle', icon: Calculator, testid: 'nav-bundle-btn' },
];

// Secondary multichain analytics tools, grouped under a "Tools" menu.
const TOOLS_NAV = [
  { to: '/analytics', label: 'Analytics', icon: BarChart3, testid: 'nav-analytics-btn' },
  { to: '/labels', label: 'Labels', icon: Tag, testid: 'nav-labels-btn' },
  { to: '/contracts', label: 'Contracts', icon: ShieldCheck, testid: 'nav-contracts-btn' },
  { to: '/archive', label: 'Archive', icon: FileText, testid: 'nav-archive-btn' },
  { to: '/firstbuys', label: 'First Buys', icon: Search, testid: 'nav-firstbuys-btn' },
];

export default function Layout({ children }) {
  const { theme, toggleTheme } = useTheme();
  const { connectionStatus: wsStatus, autoUpdateEnabled, toggleAutoUpdate } = useWebSocket();
  const [searchQuery, setSearchQuery] = useState('');
  const [indexerStatus, setIndexerStatus] = useState(null);
  const [statusErrored, setStatusErrored] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => { setMobileMenuOpen(false); setToolsOpen(false); }, [location.pathname]);

  useEffect(() => {
    const handleRefreshState = (e) => setIsRefreshing(e.detail.refreshing);
    window.addEventListener('dashboard-refresh-state', handleRefreshState);
    return () => window.removeEventListener('dashboard-refresh-state', handleRefreshState);
  }, []);

  const handleRefresh = () => window.dispatchEvent(new CustomEvent('trigger-dashboard-refresh'));

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const status = await getIndexerStatus();
        setIndexerStatus(status);
        setStatusErrored(false);
      } catch (error) {
        console.error('Failed to fetch indexer status:', error);
        setStatusErrored(true);
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  // Unified search: everything resolves through the search-first Explorer home,
  // which auto-detects token contract vs wallet vs tx hash.
  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/?q=${encodeURIComponent(q)}`);
    setSearchQuery('');
  };

  const getSyncStatus = () => {
    if (!indexerStatus) {
      return statusErrored
        ? { status: 'offline', label: 'Offline' }
        : { status: 'syncing', label: 'Connecting…' };
    }
    if (indexerStatus.lag <= 10) return { status: 'online', label: 'Synced' };
    return { status: 'syncing', label: `${indexerStatus.lag} blocks behind` };
  };
  const syncStatus = getSyncStatus();

  const isExplorerActive = location.pathname === '/' || location.pathname.startsWith('/explorer');
  const isToolActive = TOOLS_NAV.some((t) => location.pathname === t.to);
  const navActive = (to) => (to === '/' ? isExplorerActive : location.pathname === to);

  return (
    <div className="min-h-screen bg-background">
      {/* Header — glass chrome per design blueprint. */}
      <header className="sticky top-0 z-50 border-b border-border backdrop-blur-xl bg-background/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Brand */}
            <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="logo-link">
              <div className="w-9 h-9 rounded bg-primary flex items-center justify-center group-hover:scale-105 transition-transform duration-200">
                <Hexagon className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="hidden sm:block leading-tight">
                <h1 className="text-base font-bold tracking-tight">Taiboku Explorer</h1>
                <p className="mono-label text-[9px]">Multichain Analytics</p>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-1 ml-3">
              {PRIMARY_NAV.map(({ to, label, icon: Icon, testid }) => (
                <Link to={to} key={to}>
                  <Button
                    variant={navActive(to) ? 'secondary' : 'ghost'}
                    size="sm"
                    className="text-sm flex items-center gap-1.5"
                    data-testid={testid}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </Button>
                </Link>
              ))}
              <DropdownMenu open={toolsOpen} onOpenChange={setToolsOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={isToolActive ? 'secondary' : 'ghost'}
                    size="sm"
                    className="text-sm flex items-center gap-1.5"
                    data-testid="nav-tools-btn"
                  >
                    <Wrench className="w-4 h-4" /> Tools <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-44">
                  {TOOLS_NAV.map(({ to, label, icon: Icon, testid }) => (
                    <DropdownMenuItem key={to} asChild>
                      <Link to={to} data-testid={`${testid}-item`} className="flex items-center gap-2 cursor-pointer">
                        <Icon className="w-4 h-4" /> {label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* Desktop search */}
            <form onSubmit={handleSearch} className="hidden md:block flex-1 max-w-sm mx-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Token, wallet or tx…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 font-mono text-sm h-9"
                  data-testid="search-input"
                />
              </div>
            </form>

            <div className="flex-1 md:hidden" />

            {/* Right controls */}
            <div className="flex items-center gap-1.5 sm:gap-3">
              <ChainSelector />

              <div className="hidden sm:flex items-center gap-2 text-xs">
                <div className={`status-dot ${syncStatus.status}`} />
                <span className="text-muted-foreground">{syncStatus.label}</span>
              </div>

              <Button
                variant={autoUpdateEnabled ? 'default' : 'ghost'}
                size="sm"
                onClick={toggleAutoUpdate}
                className={`gap-1.5 h-8 px-2 ${autoUpdateEnabled ? 'bg-success text-background hover:opacity-90' : ''}`}
                data-testid="auto-update-toggle"
                title={autoUpdateEnabled ? 'Live updates on' : 'Enable live updates'}
              >
                {wsStatus === 'connected' ? <Radio className="w-4 h-4 animate-pulse" />
                  : wsStatus === 'connecting' ? <Wifi className="w-4 h-4 animate-pulse" />
                  : <WifiOff className="w-4 h-4" />}
                <span className="hidden sm:inline text-xs">{autoUpdateEnabled ? 'Live' : 'Auto'}</span>
              </Button>

              <Button
                variant="ghost" size="icon" onClick={handleRefresh} disabled={isRefreshing}
                data-testid="header-refresh-btn" aria-label="Refresh data" title="Refresh data"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>

              <Button
                variant="ghost" size="icon" onClick={toggleTheme}
                data-testid="theme-toggle" aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <Button
                variant="ghost" size="icon" className="md:hidden"
                onClick={() => setMobileMenuOpen((o) => !o)}
                data-testid="mobile-menu-toggle" aria-label="Toggle navigation menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {/* Mobile search */}
          <form onSubmit={handleSearch} className="md:hidden pb-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Token, wallet or tx…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 font-mono text-sm"
                data-testid="search-input-mobile"
              />
            </div>
          </form>

          {/* Mobile nav */}
          {mobileMenuOpen && (
            <nav className="md:hidden border-t border-border py-2 grid grid-cols-2 gap-1.5 pb-3" data-testid="mobile-nav-menu">
              {[...PRIMARY_NAV, ...TOOLS_NAV].map(({ to, label, icon: Icon, testid }) => (
                <Link to={to} key={to} onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant={navActive(to) ? 'secondary' : 'ghost'}
                    size="sm"
                    className="w-full justify-start text-sm flex items-center gap-2"
                    data-testid={`${testid}-mobile`}
                  >
                    <Icon className="w-4 h-4" /> {label}
                  </Button>
                </Link>
              ))}
              <div className="col-span-2 flex items-center gap-2 text-xs text-muted-foreground px-2 pt-1">
                <div className={`status-dot ${syncStatus.status}`} />
                <span>{syncStatus.label}</span>
              </div>
            </nav>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</main>

      <footer className="border-t border-border mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Activity className="w-4 h-4" />
              <span>Taiboku Multichain Explorer</span>
              <span className="text-border">•</span>
              <span>Made by Taiboku LLC © 2026</span>
            </div>
            {indexerStatus && (
              <div className="text-xs text-muted-foreground font-mono" data-testid="footer-block-height">
                Block #{indexerStatus.latest_block.toLocaleString()}
              </div>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}
